import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';

export type AccessAreaId = 'rwanda' | 'nigeria' | 'insights';

export interface AuthorizedOrganisation {
  id: number;
  name: string;
  domain: string;
}

interface PortalUser {
  email?: string;
  roles?: unknown;
  assigned_orgs?: unknown[];
  [key: string]: unknown;
}

interface LoginResponse {
  logintoken?: string[];
  token?: string;
  user?: PortalUser;
  roles?: unknown;
  assigned_orgs?: unknown[];
}

interface ProfileResponse extends PortalUser {
  user?: PortalUser;
  data?: PortalUser;
}

export interface CentralSession {
  token: string;
  email: string;
  user: PortalUser;
  roles: string[];
  organisationNames: string[];
  organisations: AuthorizedOrganisation[];
  accessAreas: AccessAreaId[];
}

@Injectable({providedIn: 'root'})
export class CentralAuthService {
  private readonly sessionKey = 'oneKashaCentralSession';

  constructor(private http: HttpClient) {}

  get session(): CentralSession | null {
    try {
      const value = localStorage.getItem(this.sessionKey);
      return value ? JSON.parse(value) as CentralSession : null;
    } catch {
      return null;
    }
  }

  async emailLogin(email: string, password: string): Promise<CentralSession> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.cmsApiUrl}/enterprise/userLogin`, {email, password})
    );
    return this.createSession(response, email);
  }

  async googleLogin(credential: string): Promise<CentralSession> {
    const email = this.emailFromGoogleCredential(credential);
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.cmsApiUrl}/inspiniaGmailLogin`, {email})
    );
    return this.createSession(response, email);
  }

  async restoreSession(): Promise<CentralSession | null> {
    const current = this.session;
    if (!current?.token) return null;
    try {
      const response = await firstValueFrom(this.http.get<ProfileResponse>(`${environment.cmsApiUrl}/profile`, {
        headers: this.authHeaders(current.token)
      }));
      const profile = response.user || response.data || response;
      const roleSource = response.roles ?? profile.roles;
      const assignmentSource = response.assigned_orgs ?? profile.assigned_orgs;
      const roles = roleSource === undefined ? current.roles : this.normalizeRoles(roleSource);
      const assignedOrgs = assignmentSource === undefined
        ? current.user.assigned_orgs || []
        : assignmentSource;
      const user: PortalUser = {
        ...current.user,
        ...profile,
        roles,
        assigned_orgs: assignedOrgs
      };
      const assignedNames = this.organisationNames(assignedOrgs);
      const organisationNames = this.isAdministrator(roles)
        ? current.organisations.map(organisation => organisation.name)
        : assignedNames;
      const updated: CentralSession = {
        ...current,
        email: profile.email || current.email,
        user,
        roles,
        organisationNames,
        accessAreas: this.resolveAccessAreas(roles, organisationNames)
      };
      this.persist(updated);
      return updated;
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.logout();
        return null;
      }
      return current;
    }
  }

  async refreshEntitlements(): Promise<CentralSession> {
    const current = this.session;
    if (!current) throw new Error('No active session.');
    const roles = this.normalizeRoles(current.roles);
    let organisations = this.organisations(current.user.assigned_orgs);
    try {
      const response = await firstValueFrom(
        this.http.get<{data?: unknown[]}>(`${environment.cmsApiUrl}/enterprise/organisations?per_page=1000`, {
          headers: this.authHeaders(current.token)
        })
      );
      const fetched = this.organisations(response.data || []);
      if (fetched.length) organisations = fetched.filter(org => this.isPlatformOrganisation(org.name));
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) throw error;
    }
    const assignedNames = this.organisationNames(current.user.assigned_orgs);
    const assignedIds = this.organisationIds(current.user.assigned_orgs);
    const assignedOrganisations = assignedIds.size
      ? organisations.filter(org => assignedIds.has(org.id))
      : organisations.filter(org => assignedNames.includes(org.name));
    const organisationNames = this.isAdministrator(roles)
      ? organisations.map(org => org.name)
      : assignedOrganisations.length
        ? assignedOrganisations.map(org => org.name)
        : assignedNames.filter(name => this.isPlatformOrganisation(name));
    const updated = {
      ...current,
      roles,
      organisationNames,
      organisations,
      accessAreas: this.resolveAccessAreas(roles, organisationNames)
    };
    this.persist(updated);
    return updated;
  }

  logout(): void {
    localStorage.removeItem(this.sessionKey);
  }

  findOrganisation(
    patterns: string[],
    exclusions: string[] = [],
    organisationIds: number[] = []
  ): AuthorizedOrganisation | null {
    const organisations = this.session?.organisations || [];
    const matchesName = (nameValue: string) => {
      const name = nameValue.toLowerCase();
      return patterns.some(pattern => name.includes(pattern.toLowerCase())) &&
        !exclusions.some(exclusion => name.includes(exclusion.toLowerCase()));
    };
    const matches = (organisation: AuthorizedOrganisation) => organisationIds.length
      ? organisationIds.includes(organisation.id)
      : matchesName(organisation.name);
    if (organisationIds.length) {
      const byConfiguredId = organisations.find(org => organisationIds.includes(org.id));
      if (byConfiguredId) return byConfiguredId;
    }
    const assignedIds = this.organisationIds(this.session?.user.assigned_orgs || []);
    const assignedById = organisations.find(org => assignedIds.has(org.id) && matches(org));
    if (assignedById) return assignedById;

    const assignedMatches = this.organisationNames(this.session?.user.assigned_orgs || [])
      .filter(matchesName)
      .map(name => name.toLowerCase());
    const assignedOrganisation = organisations.find(org =>
      assignedMatches.includes(org.name.toLowerCase())
    );
    if (assignedOrganisation) return assignedOrganisation;

    const roles = this.session?.roles || [];
    if (!this.isAdministrator(roles)) {
      return organisations.find(matches) || null;
    }
    return organisations.find(matches) || null;
  }

  async openOrganisation(organisation: AuthorizedOrganisation, configuredUrl?: string): Promise<void> {
    const current = this.session;
    if (!current) throw new Error('Your session has expired. Please sign in again.');
    const destination = configuredUrl
      ? this.validDestination(configuredUrl)
      : this.organisationDestination(organisation);
    if (!destination) {
      throw new Error(`${organisation.name} does not have a valid destination in CMS.`);
    }
    const response = await firstValueFrom(this.http.post<{token?: string}>(
      `${environment.cmsApiUrl}/enterprise/generate-token`,
      {enterprise_org_id: String(organisation.id)},
      {headers: this.authHeaders(current.token)}
    ));
    if (!response.token) throw new Error('The destination access token could not be generated.');

    const query = new URLSearchParams({
      token: response.token,
      enterprise_org_id: String(organisation.id),
      email: current.email
    }).toString();
    destination.pathname = `${destination.pathname.replace(/\/$/, '')}/verify.html`;
    destination.search = query;
    window.open(destination.toString(), '_blank', 'noopener');
  }

  openDirectUrl(configuredUrl: string): void {
    const destination = this.validDestination(configuredUrl);
    if (!destination) throw new Error('The destination URL is not valid.');
    window.open(destination.toString(), '_blank', 'noopener');
  }

  private organisationDestination(organisation: AuthorizedOrganisation): URL | null {
    return this.validDestination(organisation.domain);
  }

  private validDestination(value: string): URL | null {
    const domain = value.trim().replace(/^["']|["']$/g, '');
    if (!domain) return null;
    try {
      const destination = new URL(/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);
      const hostname = destination.hostname.toLowerCase();
      const stagingRequired = environment.cmsApiUrl.includes('//staging.');
      if (
        destination.protocol !== 'https:' ||
        ['localhost', '127.0.0.1', '::1'].includes(hostname) ||
        (stagingRequired && !hostname.startsWith('staging.'))
      ) return null;
      return destination;
    } catch {
      return null;
    }
  }

  private createSession(response: LoginResponse, fallbackEmail: string): CentralSession {
    const token = response.logintoken?.[0] || response.token;
    if (!token || !response.user) {
      throw new Error('The login response did not include a user and access token.');
    }

    const roles = this.normalizeRoles(response.roles ?? response.user.roles);
    const assignedOrgs = response.assigned_orgs ?? response.user.assigned_orgs ?? [];
    const organisations = this.organisations(assignedOrgs)
      .filter(org => this.isPlatformOrganisation(org.name));
    const organisationNames = this.organisationNames(assignedOrgs)
      .filter(name => this.isPlatformOrganisation(name));
    const user: PortalUser = {...response.user, roles, assigned_orgs: assignedOrgs};
    const session: CentralSession = {
      token,
      email: response.user.email || fallbackEmail,
      user,
      roles,
      organisationNames,
      organisations,
      accessAreas: this.resolveAccessAreas(roles, organisationNames)
    };
    this.persist(session);
    return session;
  }

  isRwandaPdsOrganisation(name: string): boolean {
    const normalized = name.toLowerCase();
    if (normalized.includes('sanofi') || this.isNigeriaOrganisation(name)) return false;
    return normalized.includes('rw pds')
      || normalized.includes('order fulfillment orchestration service')
      || normalized.includes('(ofos)')
      || (normalized.includes('cold chain dashboard')
        && !normalized.includes('demo')
        && !normalized.includes('track & trace')
        && !normalized.includes('nigeria'));
  }

  isNigeriaOrganisation(name: string): boolean {
    const normalized = name.toLowerCase();
    if (normalized.includes('sanofi')) return false;
    return normalized.includes('sc johnson')
      || normalized.includes('scjohnson')
      || normalized.includes('ng pds')
      || (normalized.includes('nigeria') && !normalized.includes('rwanda'));
  }

  isPlatformOrganisation(name: string): boolean {
    return this.isRwandaPdsOrganisation(name) || this.isNigeriaOrganisation(name);
  }

  private resolveAccessAreas(roles: string[], organisationNames: string[]): AccessAreaId[] {
    const granted = new Set<AccessAreaId>();
    const rwandaRoles = [
      'rw_pds_business',
      'rw_pds_customer_care',
      'rw_pds_fulfillment',
      'rw_pds_pharmacist',
      'rw_pds_finance'
    ];
    const nigeriaRoles = [
      'ng_pds_business',
      'ng_pds_customer_care',
      'ng_pds_fulfillment',
      'ng_pds_pharmacist',
      'ng_pds_finance'
    ];
    const scJohnsonRoles = ['sc_johnson', 'scjohnson'];
    const partnerRoles = ['partner', 'business_partners', 'business_partner'];
    const cxoRoles = ['cxo_business_user'];
    // CMS may assign any of these to Production Support (HIV same set)
    const productionSupportRoles = ['admin_eng', 'administrator', 'super_admin'];
    const insightRoles = [
      ...cxoRoles,
      ...productionSupportRoles,
      'rw_pds_business',
      'ng_pds_business',
      'rw_pds_pharmacist',
      'ng_pds_pharmacist',
      'rw_pds_fulfillment',
      'ng_pds_fulfillment'
    ];
    if (this.isAdministrator(roles)) {
      return ['rwanda', 'nigeria', 'insights'];
    }
    if (roles.some(role => insightRoles.includes(role) || scJohnsonRoles.includes(role))) {
      granted.add('insights');
    }
    if (roles.some(role => partnerRoles.includes(role) || cxoRoles.includes(role) || productionSupportRoles.includes(role))) {
      granted.add('rwanda');
      granted.add('nigeria');
    }
    if (roles.some(role => rwandaRoles.includes(role))
      || organisationNames.some(name => this.isRwandaPdsOrganisation(name))) {
      granted.add('rwanda');
    }
    if (roles.some(role => nigeriaRoles.includes(role))
      || organisationNames.some(name => this.isNigeriaOrganisation(name) && !/sc\s*johnson/i.test(name))) {
      granted.add('nigeria');
    }
    if (roles.some(role => scJohnsonRoles.includes(role))
      || organisationNames.some(name => /sc\s*johnson/i.test(name))) {
      granted.add('insights');
    }
    return [...granted];
  }

  private normalizeRoles(value: unknown): string[] {
    let roles: unknown[] = [];
    if (typeof value === 'string') roles = value.split(',');
    else if (Array.isArray(value)) roles = value;
    else if (value && typeof value === 'object') roles = Object.values(value);
    return roles
      .flatMap(role => {
        if (typeof role === 'string') return [role];
        if (role && typeof role === 'object') {
          const candidate = role as Record<string, unknown>;
          return [
            candidate['slug'] ??
            candidate['role_slug'] ??
            candidate['name'] ??
            candidate['role'] ??
            candidate['type'] ??
            ''
          ];
        }
        return [];
      })
      .map(role => String(role).trim().toLowerCase())
      .map(role => role.replace(/[\s-]+/g, '_'))
      .map(role => ({
        scjohnson: 'sc_johnson',
        business_partner: 'business_partners',
        cxo_business_user: 'cxo_business_user',
        production_support: 'admin_eng',
        prod_support_engineer: 'admin_eng',
        production_support_engineer: 'admin_eng',
        admineng: 'admin_eng',
        superadmin: 'super_admin'
      }[role] || role))
      .filter(Boolean);
  }

  private organisationIds(value: unknown): Set<number> {
    if (!Array.isArray(value)) return new Set<number>();
    return new Set(value.flatMap(item => {
      if (typeof item === 'number' || (typeof item === 'string' && /^\d+$/.test(item.trim()))) {
        const id = Number(item);
        return Number.isFinite(id) ? [id] : [];
      }
      if (!item || typeof item !== 'object') return [];
      const record = item as Record<string, unknown>;
      const nested = record['organisation'];
      const source = nested && typeof nested === 'object'
        ? nested as Record<string, unknown>
        : record;
      const pivot = record['pivot'];
      const assignment = record['assignment'];
      const relation = pivot && typeof pivot === 'object'
        ? pivot as Record<string, unknown>
        : assignment && typeof assignment === 'object'
          ? assignment as Record<string, unknown>
          : {};
      const id = Number(
        source['id'] ?? record['org_id'] ?? record['organisation_id'] ??
        record['enterprise_org_id'] ?? relation['org_id'] ??
        relation['organisation_id'] ?? relation['enterprise_org_id']
      );
      return Number.isFinite(id) ? [id] : [];
    }));
  }

  private organisationNames(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => {
        if (typeof item === 'string') return item.trim();
        if (!item || typeof item !== 'object') return '';
        const record = item as Record<string, unknown>;
        const nested = record['organisation'];
        const source = nested && typeof nested === 'object'
          ? nested as Record<string, unknown>
          : record;
        return String(source['name'] || '').trim();
      })
      .filter(Boolean);
  }

  private organisations(value: unknown): AuthorizedOrganisation[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const record = item as Record<string, unknown>;
        const nested = record['organisation'];
        const source = nested && typeof nested === 'object'
          ? nested as Record<string, unknown>
          : record;
        const id = Number(
          source['id'] ?? record['org_id'] ?? record['organisation_id'] ?? record['enterprise_org_id']
        );
        const name = String(source['name'] || '').trim();
        const domain = String(source['domain'] || '').trim();
        return Number.isFinite(id) && name && domain ? {id, name, domain} : null;
      })
      .filter((org): org is AuthorizedOrganisation => !!org);
  }

  private isAdministrator(roles: string[]): boolean {
    return roles.some(role =>
      role === 'admin_eng' || role === 'administrator' || role === 'super_admin'
    );
  }

  private emailFromGoogleCredential(token: string): string {
    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = decodeURIComponent(
        atob(normalized).split('').map(char =>
          `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
        ).join('')
      );
      const data = JSON.parse(decoded) as {email?: string; exp?: number};
      if (!data.email || (data.exp && data.exp < Math.floor(Date.now() / 1000))) {
        throw new Error();
      }
      return data.email;
    } catch {
      throw new Error('The Google sign-in credential is invalid or expired.');
    }
  }

  private authHeaders(token: string): HttpHeaders {
    return new HttpHeaders({Authorization: `Bearer ${token}`, Accept: 'application/json'});
  }

  private persist(session: CentralSession): void {
    localStorage.setItem(this.sessionKey, JSON.stringify(session));
  }
}
